import { useState, useEffect, useContext, useRef, lazy, Suspense } from "react";
import Modal from "react-modal";
import { useNavigate } from "react-router-dom";

// Project modules
import { GameContext } from "../../Context/GameContext";
import useEscapeRoomApi from "../../Hooks/useEscapeRoomApi";
import { RoomStatus } from "../../Types/Game";
import ControlButtons from "../../Components/ControlButtons/ControlButtons";
import Timer from "../../Components/Timer/Timer";
import HostCharacter from "../../Components/Characters/HostCharacter";
import useBackButtonWarning from "../../Hooks/useBackButtonWarning";
import "./EscapeRoom.css";

const Room1 = lazy(() => import("../../Components/Rooms/Room1/Room1"));
const Room2 = lazy(() => import("../../Components/Rooms/Room2/Room2"));
const Room3 = lazy(() => import("../../Components/Rooms/Room3/Room3"));
const Room4 = lazy(() => import("../../Components/Rooms/Room4/Room4"));
const Room5 = lazy(() => import("../../Components/Rooms/Room5/Room5"));
const Room6 = lazy(() => import("../../Components/Rooms/Room6/Room6"));
const Room7 = lazy(() => import("../../Components/Rooms/Room7/Room7"));
const Room8 = lazy(() => import("../../Components/Rooms/Room8/Room8"));
const Room9 = lazy(() => import("../../Components/Rooms/Room9/Room9"));
const ExperimentRoom = lazy(() => import("../../Components/Rooms/ExperimentRoom/ExperimentRoom"));

/**
 * EscapeRoom
 * ----------
 * Master container for the multi-room experience:
 * - Chooses the active room component from context.
 * - Orchestrates help/clue UI and the countdown timer.
 * - Advances to the next room, and records the result on completion.
 */

// TYPES -------------------------------------------------------------
type RoomId = "room1" | "room2" | "room3" | "room4" | "room5" | "room6" | "room7" | "room8" | "room9" | "room99";

/** room99 is the developer Experiment Room: no timer, no results recorded. */
const EXPERIMENT_ROOM_ID: RoomId = "room99";

type RoomComponentProps = {
  navigateNext: () => void;
  setHelpContent: React.Dispatch<React.SetStateAction<string>>;
  setClueContent: React.Dispatch<React.SetStateAction<string>>;
  isLastRoom: boolean;
};

// ROOM MAP ----------------------------------------------------------
const roomComponents: Record<RoomId, React.LazyExoticComponent<React.ComponentType<RoomComponentProps>>> = {
  room1: Room1,
  room2: Room2,
  room3: Room3,
  room4: Room4,
  room5: Room5,
  room6: Room6,
  room7: Room7,
  room8: Room8,
  room9: Room9,
  room99: ExperimentRoom,
};

// STYLES ------------------------------------------------------------
const customModalStyles: Modal.Styles = {
  overlay: { zIndex: 1 },
  content: { zIndex: 1 },
};

Modal.setAppElement("#root");

// COMPONENT ---------------------------------------------------------
const EscapeRoom: React.FC = () => {
  /** CONTEXT ------------------------------------------------------- */
  const {
    currentRoom,
    setCurrentRoom,
    setIsWinnerAccessible,
    isTimeUp,
    teamName,
    profileSettings,
    roomStates,
    setProfileSettings,
    eventName,
    setStartTime,
  } = useContext(GameContext);

  // Safe local aliases: both can be undefined transiently while the profile
  // loads (see GameContext), but by the time a room is actually rendered
  // they're expected to be populated. Guarding here (rather than scattering
  // `?.`/`??` at every call site) keeps the room-navigation logic readable.
  const rooms = roomStates ?? {};
  const escapeRoomTime = profileSettings?.escapeRoomTime ?? 0;
  const isSingleRoom = profileSettings?.singleRoom ?? false;

  /** STATE --------------------------------------------------------- */
  const [helpContent, setHelpContent] = useState<string>("");
  const [isHelpVisible, setIsHelpVisible] = useState<boolean>(false);
  const [hasHelpBeenClosed, setHasHelpBeenClosed] = useState<boolean>(false);

  const [clueContent, setClueContent] = useState<string>("");
  const [isClueModalOpen, setIsClueModalOpen] = useState<boolean>(false);

  // Zoom level for the room viewport, so a room too wide for the browser can be scaled to fit.
  const [zoomLevel, setZoomLevel] = useState<number>(1);
  const minZoom = 0.4;
  const maxZoom = 1;
  const zoomStep = 0.1;

  // Warn before an in-app browser-back navigation loses game progress (ERD-32)
  const { isPromptOpen: isLeavePromptOpen, closePrompt: closeLeavePrompt } =
    useBackButtonWarning(true);

  // Only one robot speech bubble on screen at a time
  useEffect(() => {
    if (isLeavePromptOpen) setIsHelpVisible(false);
  }, [isLeavePromptOpen]);

  /** REFS ---------------------------------------------------------- */
  // keep latest remaining time accessible across handlers
  const remainingTimeRef = useRef<number>(escapeRoomTime);

  /** EFFECTS ------------------------------------------------------- */
  // Show initial help when room changes
  useEffect(() => {
    const helpMessages: Record<RoomId, string> = {
      room1:
        "A four-letter word is hidden somewhere in this room. Solve the clues to find the correct word then enter it into the keypad to unlock the door.",
      room2:
        "A four-letter word is hidden somewhere in this room. Solve the clues to find the correct word then enter it into the keypad to unlock the door.",
      room3:
        "A four-letter word is hidden somewhere in this room. Solve the clues to find the correct word then enter it into the keypad to unlock the door.",
      room4:
        "A four-letter word is hidden somewhere in this room. Solve the clues to find the correct word then enter it into the keypad to unlock the door.",
      room5:
        "Search the room for four hidden letters. Combine the letters you find to make a word, then enter it into the keypad to unlock the door.",
      room6:
        "Search the room for four hidden letters. Combine the letters you find to make a word, then enter it into the keypad to unlock the door.",
      room7:
        "Can you identify the last person who logged into the computer? Find their four letter password and enter it into the keypad to unlock the door.",
      room8:
        "Can you convince Dorky.AI that you are human? Only by completing all of the puzzles and challenges in the room will Dorky.AI give up the door code!",
      room9:
        "Explore Elsie's study and complete its activities to fill in the four missing answers of the crossword pinned to the corkboard. Solve it and it will reveal the door code — enter it into the keypad to escape.",
      room99:
        "This is the Experiment Room — a testing space for activities in development. Click any highlighted object to try an activity. There is no timer and the door is always open.",
    };

    setHelpContent(helpMessages[currentRoom as RoomId]);
    setIsHelpVisible(true);
  }, [currentRoom]);

  // Reset zoom to full when a new room loads
  useEffect(() => {
    setZoomLevel(1);
  }, [currentRoom]);

  // Guard against page unload + time-up redirect
  const navigate = useNavigate();
  const { setEscapeResult } = useEscapeRoomApi();

  useEffect(() => {
    const beforeUnloadListener = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue =
        "Are you sure you want to leave? You will lose your progress.";
    };

    window.addEventListener("beforeunload", beforeUnloadListener);

    // If page was refreshed (legacy nav API), push back to root
    // (note: the legacy Navigation Timing API may not exist everywhere)
    interface LegacyPerformanceNavigation {
      navigation?: { type?: number };
    }
    if ((window.performance as unknown as LegacyPerformanceNavigation)?.navigation?.type === 1) {
      window.history.replaceState(null, "", "/");
    }

    // The Experiment Room is timer-exempt: never trap while it's active.
    if (isTimeUp && currentRoom !== EXPERIMENT_ROOM_ID) {
      setIsWinnerAccessible(false);
      navigate("/trapped");
    }

    return () => {
      window.removeEventListener("beforeunload", beforeUnloadListener);
    };
  }, [isTimeUp, navigate, setIsWinnerAccessible, currentRoom]);

  /** HANDLERS ------------------------------------------------------ */
  const updateRemainingTime = (newRemainingTime: number) => {
    remainingTimeRef.current = newRemainingTime;
  };

  // Manual toggle of help after the initial display
  const toggleHelp = () => {
    const tips: Record<RoomId, string> = {
      room1: "The note on the desk looks like a good place to start!",
      room2: "The brain in the jar, that looks like a good place to start!",
      room3: "The notice board looks like it could be important!",
      room4: "There might be equipment in the room to help you see through the smoke!",
      room5: "The poster of robots on the wall might be a good place to start!",
      room6: "The laptop on the desk looks like a good place to start!",
      room7: "The items on the desk might be a good place to start!",
      room8: "The computer in the corner might be a good place to start!",
      room9: "Stuck on one activity? Every crossword answer can also be found somewhere else in the room.",
      room99: "Click any highlighted object to launch its activity. Use the door whenever you're done.",
    };
    setHelpContent(tips[currentRoom as RoomId]);
    setIsHelpVisible((prev) => !prev);
  };

  // First-time closing of the help starts the timer
  const closeHelpContent = () => {
    setIsHelpVisible(false);
    if (!hasHelpBeenClosed) {
      setHasHelpBeenClosed(true);
      setStartTime(Math.floor(Date.now() / 1000));
    }
  };

  const closeClueModal = () => setIsClueModalOpen(false);

  // Zoom controls for the room viewport
  const handleZoomIn = () => setZoomLevel((prev) => Math.min(maxZoom, +(prev + zoomStep).toFixed(2)));
  const handleZoomOut = () => setZoomLevel((prev) => Math.max(minZoom, +(prev - zoomStep).toFixed(2)));
  const handleZoomReset = () => setZoomLevel(1);

  // Advance to next room or finish and record result
  const navigateNext = () => {
    const activeRooms = Object.keys(rooms).filter(
      (room) => rooms[room].active
    ) as RoomId[];

    const currentIndex = activeRooms.indexOf(currentRoom as RoomId);

    // campaign completion or single-room mode
    if (currentIndex === activeRooms.length - 1 || isSingleRoom) {
      const remainingTime = remainingTimeRef.current;
      const timeTaken = escapeRoomTime - remainingTime;

      // teamName/eventName are expected to be set by this point (Start screen
      // requires both before play begins) — guard defensively rather than
      // submitting a result with a missing name/event.
      // Experiment Room completions are never recorded (test data would
      // pollute the leaderboard).
      if (currentRoom === EXPERIMENT_ROOM_ID) {
        // no-op: skip results recording
      } else if (!teamName || !eventName) {
        console.error(
          "Cannot record escape result: missing teamName or eventName",
          { teamName, eventName }
        );
      } else {
        setEscapeResult({
          name: teamName,
          event: eventName,
          timeTaken,
          room: isSingleRoom
            ? (currentRoom as RoomId)
            : activeRooms.toString(),
        });
      }

      setIsWinnerAccessible(true);

      setProfileSettings((prev) =>
        prev
          ? {
              ...prev,
              rooms: {
                ...prev.rooms,
                [currentRoom as RoomId]: {
                  ...prev.rooms[currentRoom as RoomId],
                  status: RoomStatus.Completed,
                },
              },
            }
          : prev
      );

      navigate("/winner", { state: { timeTaken, remainingTime } });
    } else if (currentIndex !== -1 && currentIndex < activeRooms.length - 1) {
      // mark current as complete and move forward
      setProfileSettings((prev) =>
        prev
          ? {
              ...prev,
              rooms: {
                ...prev.rooms,
                [currentRoom as RoomId]: {
                  ...prev.rooms[currentRoom as RoomId],
                  status: RoomStatus.Completed,
                },
              },
            }
          : prev
      );
      setCurrentRoom(activeRooms[currentIndex + 1]);
    }
  };

  /** DERIVED ------------------------------------------------------- */
  const ActiveRoomComponent = roomComponents[currentRoom as RoomId];

  // Active list + “last room?” flag
  const activeRooms = Object.keys(rooms).filter(
    (r) => rooms[r].active
  ) as RoomId[];

  const isLastRoom =
  activeRooms.length > 0 &&
  activeRooms.indexOf(currentRoom as RoomId) === activeRooms.length - 1;

  // Experiment Room is untimed: pause and hide the countdown while inside.
  const isExperimentRoom = currentRoom === EXPERIMENT_ROOM_ID;

  /** RENDER -------------------------------------------------------- */
  return (
    <div className="escape-room">
      <div id="escapeRoomContainer">
        <ControlButtons />
        <div className="room-viewport" style={{ transform: `scale(${zoomLevel})` }}>
          {ActiveRoomComponent && (
            <Suspense fallback={<div className="room-loading">Loading room...</div>}>
              <ActiveRoomComponent
                navigateNext={navigateNext}
                setHelpContent={setHelpContent}
                setClueContent={setClueContent}
                isLastRoom={isLastRoom}
              />
            </Suspense>
          )}
        </div>
      </div>

      {/* Clue Modal */}
      <Modal
        isOpen={isClueModalOpen}
        onRequestClose={closeClueModal}
        contentLabel="Clue Modal"
        style={customModalStyles}
      >
        <button onClick={closeClueModal} aria-label="Close clue modal">
          &times;
        </button>
        <p>{clueContent}</p>
      </Modal>

      {/* Side panel */}
      <div className="side-info">
        <div className="side-info__rooms">
          <p>Rooms</p>
          <div className="room-icons">
            {Object.keys(rooms)
              .filter((room) => rooms[room].active)
              .map((room) => (
                <div
                  key={room}
                  className={`room-icon ${room} ${
                    currentRoom === room ? "active" : ""
                  }`}
                />
              ))}
          </div>
        </div>

        {/* Help button */}
        <button className="button-help" onClick={toggleHelp}>
          <span className="icon" />
          <span className="button-text">Help</span>
        </button>

        {/* Room zoom controls */}
        <div className="zoom-controls">
          <button
            className="zoom-out"
            onClick={handleZoomOut}
            disabled={zoomLevel <= minZoom}
            aria-label="Zoom out"
          >
            &minus;
          </button>
          <button
            className="zoom-reset"
            onClick={handleZoomReset}
            disabled={zoomLevel === 1}
            aria-label="Reset zoom"
          >
            &#x21bb;
          </button>
          <button
            className="zoom-in"
            onClick={handleZoomIn}
            disabled={zoomLevel >= maxZoom}
            aria-label="Zoom in"
          >
            &#43;
          </button>
        </div>

        {!isExperimentRoom && (
          <div className="side-info__timer">
            <p>Time remaining</p>
            <Timer
              updateRemainingTime={updateRemainingTime}
              paused={!hasHelpBeenClosed} // Timer is paused until first help closed
            />
          </div>
        )}
      </div>

      {/* Host helper */}
      <HostCharacter
        isHelpVisible={isHelpVisible}
        helpContent={helpContent}
        closeHelpContent={closeHelpContent}
        buttonLabel={!hasHelpBeenClosed ? "Start the challenge!" : "Close Help"}
      />

      {/* Leave-game confirmation (back button pressed mid-game) */}
      <HostCharacter
        isHelpVisible={isLeavePromptOpen}
        helpContent="Going back will lose your progress in this challenge. Are you sure you want to leave?"
        closeHelpContent={closeLeavePrompt}
        buttonLabel="Stay and keep playing"
        secondaryButtonLabel="Leave"
        onSecondaryAction={() => navigate("/")}
        closeOnBackdropClick={false}
      />
    </div>
  );
};

export default EscapeRoom;